import {
  AddIcon,
  Button,
  ButtonGroup,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  EditIcon,
  Row,
  Table,
  TableColumns,
  Tooltip,
  useDefaultSelecting,
} from '@bp/ui-components';
import { RowSelectionState } from '@tanstack/react-table';
import { observer } from 'mobx-react-lite';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CombinedError } from 'urql';
import { AppLayoutContext } from '../../../contexts/AppLayoutContextProvider';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useConfirm } from '../../../hooks/useConfirm';
import { useHiddenColumns } from '../../../hooks/useHiddenColumns';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import { showSuccessDeleteToast, showSuccessDuplicateToast, showUserErrorToast } from '../../../utils/toast';
import { getTypeChecktArray } from '../../../utils/typeguards';
import { LessonCard } from '../../LessonCard/LessonCard';
import { estimatedRowHeightByCards } from '../../Lessons/utils/estimatedRowHeight';
import { useCurriculum } from '../hooks/useCurriculum';
import { CurriculumBlockContainerModal } from '../modals/CurriculumBlockContainerModal';
import { CurriculumModal } from '../modals/CurriculumModal';
import { CardPrintInfo, CurriculaTableDataType } from '../types';

type CurriculumOverviewTableProps = {
  curriculumUuid: string | null;
  grade?: string | undefined;
  subjectUuid?: string | undefined;
};

export const CurriculumOverviewTable = observer(
  ({ curriculumUuid, grade, subjectUuid }: CurriculumOverviewTableProps) => {
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const { pimAuthClaims } = useAuthClaims();
    const schoolYear = useSelectedSchoolYear();
    const permissions = usePermissionChecker();
    const readonly = !permissions?.canWriteDeputate();
    const userConfigKey = 'curriculum-table';

    const { confirm, ConfirmationDialog } = useConfirm();
    const { scrollRef } = useContext(AppLayoutContext);

    const [currentCurriculumUuid, setCurrentCurriculumUuid] = useState<string | null>(null);
    const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
    const [epochModalOpen, setEpochModalOpen] = useState<boolean>(false);

    const { columnVisibility, saveColumnVisibility } = useHiddenColumns(userConfigKey, {
      'lesson.groups': true,
      classes: false,
      groupNames: false,
      'subjectHours.subjectHour_other': true,
      'subjectHours.studentHours': true,
      'subjectHours.teacherHours': true,
    });

    const { sorting, saveSorting } = useColumnsSort(userConfigKey);

    const { rowSelection, onRowSelectionChange, clearSelection } = useDefaultSelecting();

    const { updateCheckboxes, deleteCurriculum, queryData, tableData, loadingState, duplicateCurriculum } =
      useCurriculum({
        versionUuid: curriculumUuid ?? '',
        grade: grade,
        subjectUuid: subjectUuid,
      });

    const handleAddCurriculum = () => {
      setCurrentCurriculumUuid(null);
      setEditModalOpen(true);
    };

    const handleEditCurriculum = (uuid: string) => {
      const currentCurriculum = queryData?.curricula.find((curriculum) => curriculum.uuid === uuid);
      setCurrentCurriculumUuid(uuid);

      if (currentCurriculum && !currentCurriculum.subject.isSubjectContainer) {
        // is a Lesson / Zuordnung
        setEditModalOpen(true);
      } else {
        // is EpochenPlan
        setEpochModalOpen(true);
      }
    };

    const handleClose = () => {
      setCurrentCurriculumUuid(null);
      setEditModalOpen(false);
      setEpochModalOpen(false);
    };

    const handleDelete = async (curricula: CurriculaTableDataType[]) => {
      const confirmed = await confirm({ message: t('curriculum.confirm.delete.default_one') });
      if (confirmed) {
        const { error } = await deleteCurriculum(curricula);
        if (error) {
          showUserErrorToast({ error });
        } else {
          showSuccessDeleteToast(getTypeChecktArray<string>(curricula.map((c) => c.displayName)));
        }
      }
    };

    const handleDuplicate = async (uuids: string[]) => {
      const res = await duplicateCurriculum(uuids);
      const resultsWithError = res.filter((r) => r.error);
      if (resultsWithError && resultsWithError.length > 0) {
        const error = resultsWithError[0].error as CombinedError;
        showUserErrorToast({ text: t('common.errorToastText'), error });
      } else {
        showSuccessDuplicateToast();
      }
    };

    const columns: TableColumns<CurriculaTableDataType>[] = [
      {
        header: t('subject.title', { count: 1 }),
        id: 'subject',
        meta: {
          filterName: t('subject.title', { count: 1 }),
        },
        size: 150,
        cell: (cell) => {
          return <Tooltip content={cell.row.original.subject}>{cell.row.original.subject}</Tooltip>;
        },
        accessorFn: (cell) => cell.subject,
      },
      {
        header: t('common.type'),
        id: 'type',
        isMultiline: true,
        size: 100,
        cell: ({ row }) => {
          if (row.original.isSubjectContainerLesson) {
            return t('epochs.plan');
          }
          const cardInfos = row.original.cardsInfos;
          return (
            <>
              {cardInfos && cardInfos.defaultCards.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--spacing-4)',
                    margin: 'var(--spacing-2) 0',
                  }}
                >
                  {cardInfos.defaultCards.map((group) => {
                    const card = group[0];
                    return (
                      <div key={card.uuid} title={card.typeLabel}>
                        {card.typeLabelShort}
                      </div>
                    );
                  })}
                </div>
              )}
              {cardInfos && cardInfos.subjectContainerCards.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--spacing-4)',
                    margin: 'var(--spacing-2) 0',
                  }}
                >
                  {cardInfos.subjectContainerCards.map((group) => {
                    const card = group[0];
                    return (
                      <div key={card.uuid} title={card.typeLabel}>
                        {card.typeLabelShort}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          );
        },
        accessorFn: ({ cardsInfos, uuid }) => {
          const isEpochPlan = queryData?.curricula.find((l) => l.uuid === uuid)?.subject.isSubjectContainer ?? true;
          const sum = (cardsInfos?.defaultCards.length ?? 0) + (cardsInfos?.subjectContainerCards.length ?? 0);
          let sortValue = sum > 0 ? `${sum}_` : `${sum}`;

          if (isEpochPlan) {
            sortValue += t('epochs.plan');
          }
          if (cardsInfos && cardsInfos.defaultCards.length > 0) {
            sortValue += cardsInfos.defaultCards
              .map((dc) => {
                return dc.map((c) => c.typeLabelShort).join(',');
              })
              .join(', ');
          }
          if (cardsInfos && cardsInfos.subjectContainerCards.length > 0) {
            sortValue += cardsInfos.subjectContainerCards
              .map((dc) => {
                return dc.map((c) => c.typeLabelShort).join(',');
              })
              .join(', ');
          }
          return sortValue;
        },
      },
      {
        id: 'cards',
        isMultiline: true,
        enableSorting: false,
        canOverflow: true,
        enableColumnFilter: false,
        size: 150,
        header: t('curriculum.title', { count: 1 }),
        cell: ({ row }) => {
          const cardInfos = row.original.cardsInfos;
          return (
            <>
              {cardInfos && cardInfos.defaultCards.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--spacing-4)',
                    margin: 'var(--spacing-2) 0',
                  }}
                >
                  {cardInfos.defaultCards.map((group) => {
                    const card = group[0];
                    return (
                      <div key={card.uuid} title={card.cardLabel}>
                        <LessonCard
                          label={card.cardLabelShort}
                          lessonClasses={card.lesson}
                          count={card.counter}
                          teacherColors={card.colors}
                          duration={card.duration}
                          showHover={false}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
              {cardInfos && cardInfos.subjectContainerCards.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--spacing-4)',
                    margin: 'var(--spacing-2) 0',
                  }}
                >
                  {cardInfos.subjectContainerCards.map((group) => {
                    const card = group[0];
                    return (
                      <div key={card.uuid} title={card.cardLabel}>
                        <LessonCard
                          label={card.cardLabelShort}
                          lessonClasses={card.lesson}
                          count={card.counter}
                          teacherColors={card.colors}
                          duration={card.duration}
                          showHover={false}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          );
        },
        accessorFn: ({ cardsInfos }) => {
          const cardsToShow: CardPrintInfo[] = [];
          if (cardsInfos && cardsInfos.defaultCards.length > 0) {
            cardsInfos.defaultCards.forEach((group) => {
              cardsToShow.push({
                count: group[0].counter,
                duration: group[0].duration,
                hasSubjectContainer: false,
              });
            });
          }
          if (cardsInfos && cardsInfos.subjectContainerCards.length > 0) {
            cardsInfos.subjectContainerCards.forEach((group) => {
              cardsToShow.push({
                shortName: group[0].typeLabelShort,
                count: group[0].counter,
                duration: group[0].duration,
                hasSubjectContainer: true,
              });
            });
          }
          return cardsToShow
            .map(
              (card) =>
                `${card.shortName ? card.shortName + '\n' : ''}${card.count} x ${card.duration} ${
                  card.hasSubjectContainer
                    ? t('common.week', { count: card.duration })
                    : t('subjectHours.title', { context: 'short' })
                }`,
            )
            .join('\n\n');
        },
      },
      {
        header: `${t('classes.title', { count: 2 })}/${t('groups.title', { count: 2 })}`,
        id: 'grades',
        cell: (cell) => {
          return (
            <Tooltip
              content={
                cell.row.original.groupGradeNames
                  ?.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
                  .join(' | ') ?? ''
              }
            >
              {cell.row.original.groupGradeNames
                ?.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
                .join(' | ') ?? ''}
            </Tooltip>
          );
        },
        accessorFn: (data) =>
          data.groupGradeNames
            ?.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
            .join(' | ') ?? '',
        meta: {
          filterName: t('classes.level.full'),
        },
        filterFn: 'arrIncludes',
        size: 150,
      },
      {
        header: t('persons.title', { count: 2 }),
        id: 'persons',
        accessorKey: 'curriculumInfo.teachersCount',
        alignment: 'right',
        size: 60,
      },
      {
        header: t('deputate.title', { count: 1 }),
        id: 'deputate',
        accessorFn: (cell) => (cell.curriculumInfo?.deputat ?? 0).toFixed(2),
        cell: ({ row }) => (row.original.curriculumInfo?.deputat ?? 0).toFixed(2),
        alignment: 'right',
        size: 60,
      },
      {
        header: t('subjectHours.title', { context: 'short' }),
        id: 'subjectHours',
        meta: {
          tooltip: t('subjectHours.title', { count: 2 }),
        },
        accessorFn: (cell) => (cell.curriculumInfo?.studentCards ?? 0).toFixed(2),
        cell: ({ row }) => (row.original.curriculumInfo?.studentCards ?? 0).toFixed(2),
        alignment: 'right',
        size: 60,
      },
      {
        header: t('subjectHours.studentHours', { context: 'short' }),
        id: 'studentHours',
        meta: {
          tooltip: t('subjectHours.studentHours'),
        },
        accessorFn: (cell) => (cell.curriculumInfo?.studentHours ?? 0).toFixed(2),
        cell: ({ row }) => (row.original.curriculumInfo?.studentHours ?? 0).toFixed(2),
        alignment: 'right',
        size: 60,
      },
      {
        header: t('subjectHours.teacherHours', { context: 'short' }),
        id: 'teacherHours',
        meta: {
          tooltip: t('subjectHours.teacherHours'),
        },
        accessorFn: (cell) => (cell.curriculumInfo?.teacherHours ?? 0).toFixed(2),
        cell: ({ row }) => (row.original.curriculumInfo?.teacherHours ?? 0).toFixed(2),
        alignment: 'right',
        size: 60,
      },
      {
        header: t('lesson.basics.isElective'),
        id: 'elective',
        accessorKey: 'elective',
        type: 'boolean',
        size: 60,
      },
      {
        header: t('lesson.basics.isTeachingLoadEnabled.short'),
        meta: {
          filterName: t('lesson.basics.isTeachingLoadEnabled.full'),
          tooltip: t('lesson.basics.isTeachingLoadEnabled.full'),
        },
        id: 'teachingLoadEnabled',
        accessorKey: 'teachingLoadEnabled',
        type: 'boolean',
        size: 60,
      },
      {
        header: t('lesson.basics.isTimetableEnabled.short'),
        meta: {
          filterName: t('lesson.basics.isTimetableEnabled.full'),
          tooltip: t('lesson.basics.isTimetableEnabled.full'),
        },
        id: 'timetableEnabled',
        accessorKey: 'timetableEnabled',
        type: 'boolean',
        size: 60,
      },
    ];

    const dropdownItems = (row: Row<CurriculaTableDataType>): DropdownMenuItem[] => {
      const uuid = row.original.uuid;

      return [
        {
          label: t('lesson.actions.toggle.isElective'),
          type: 'switch',
          value: row.original.elective ?? false,
          onValueChange: async () => {
            await updateCheckboxes({
              type: 'elective',
              uuids: [uuid],
              value: !row.original.elective,
            });
          },
        },
        {
          label: t('lesson.basics.isTeachingLoadEnabled.full'),
          type: 'switch',
          value: row.original.teachingLoadEnabled ?? false,
          onValueChange: async () => {
            await updateCheckboxes({
              type: 'teachingLoadEnabled',
              uuids: [uuid],
              value: !row.original.teachingLoadEnabled,
            });
          },
        },
        {
          label: t('lesson.basics.isTimetableEnabled.full'),
          type: 'switch',
          value: row.original.timetableEnabled ?? false,
          onValueChange: async () => {
            await updateCheckboxes({
              type: 'timetableEnabled',
              uuids: [uuid],
              value: !row.original.timetableEnabled,
            });
          },
        },
        {
          type: 'ruler',
        },
        {
          type: 'ruler',
        },
        {
          label: t('common.duplicate'),
          onClick: async () => {
            await handleDuplicate([uuid]);
          },
        },
        {
          type: 'ruler',
        },
        {
          label: t('common.delete'),
          color: 'error',
          onClick: async () => {
            await handleDelete([row.original]);
          },
        },
      ];
    };

    const addDropdownItems: DropdownMenuItem[] = [
      {
        label: t('epochs.plan'),
        onClick: () => handleEditCurriculum(currentCurriculumUuid ?? ''),
      },
      {
        label: t('curriculum.title', { count: 1 }),
        onClick: handleAddCurriculum,
      },
    ];

    //biome-ignore lint/correctness/useExhaustiveDependencies(t): ignore t
    //biome-ignore lint/correctness/useExhaustiveDependencies(handleDelete): ignore handleDelete
    //biome-ignore lint/correctness/useExhaustiveDependencies(handleDeleteLessons): ignore handleDeleteLessons
    //biome-ignore lint/correctness/useExhaustiveDependencies(handleDuplicate): ignore handleDuplicate
    const bulkEdit = useCallback(
      (selectedFlatRows: Row<CurriculaTableDataType>[]) => {
        return (
          <DropdownMenu
            data={[
              {
                label: t('lesson.basics.isElective'),
                subContent: [
                  {
                    label: t('common.activateAll'),
                    onClick: async () => {
                      await updateCheckboxes({
                        type: 'elective',
                        uuids: selectedFlatRows.map(({ original }) => original.uuid),
                        value: true,
                      });
                      clearSelection();
                    },
                  },
                  {
                    label: t('common.deactivateAll'),
                    onClick: async () => {
                      await updateCheckboxes({
                        type: 'elective',
                        uuids: selectedFlatRows.map(({ original }) => original.uuid),
                        value: false,
                      });
                      clearSelection();
                    },
                  },
                ],
              },
              {
                label: t('lesson.basics.isTimetableEnabled.full'),
                subContent: [
                  {
                    label: t('common.activateAll'),
                    onClick: async () => {
                      await updateCheckboxes({
                        type: 'timetableEnabled',
                        uuids: selectedFlatRows.map(({ original }) => original.uuid),
                        value: true,
                      });
                    },
                  },
                  {
                    label: t('common.deactivateAll'),
                    onClick: async () => {
                      await updateCheckboxes({
                        type: 'timetableEnabled',
                        uuids: selectedFlatRows.map(({ original }) => original.uuid),
                        value: false,
                      });
                      clearSelection();
                    },
                  },
                ],
              },
              {
                label: t('lesson.basics.isTeachingLoadEnabled.full'),
                subContent: [
                  {
                    label: t('common.activateAll'),
                    onClick: async () => {
                      await updateCheckboxes({
                        type: 'teachingLoadEnabled',
                        uuids: selectedFlatRows.map(({ original }) => original.uuid),
                        value: true,
                      });
                      clearSelection();
                    },
                  },
                  {
                    label: t('common.deactivateAll'),
                    onClick: async () => {
                      await updateCheckboxes({
                        type: 'teachingLoadEnabled',
                        uuids: selectedFlatRows.map(({ original }) => original.uuid),
                        value: false,
                      });
                      clearSelection();
                    },
                  },
                ],
              },
              { type: 'ruler' },
              { type: 'ruler' },
              {
                label: t('common.duplicate'),
                disabled: selectedFlatRows.length > 30,
                onClick: async () => {
                  const uuids = selectedFlatRows.map(({ original }) => original.uuid);
                  await handleDuplicate(uuids);
                  clearSelection();
                },
              },
              { type: 'ruler' },
              {
                label: t('common.delete'),
                color: 'error',
                onClick: async () => {
                  await handleDelete(selectedFlatRows.map(({ original }) => original));
                  clearSelection();
                },
              },
            ]}
          />
        );
      },
      [updateCheckboxes, clearSelection],
    );

    const rowMarker = useMemo(() => {
      const marker: RowSelectionState = {};
      tableData.forEach((lesson, index) => {
        if (lesson.isSubjectContainerLesson) {
          marker[index] = true;
        }
      });
      return marker;
    }, [tableData]);

    return (
      <>
        <Table<CurriculaTableDataType>
          data={tableData}
          // mobil nicht: der Kopf klebt per position: fixed und liefe beim seitlichen Wischen nicht mit
          stickyHeader={!isMobile}
          stickyHeaderOptions={{
            top: 60, // height: var(--header-height)
          }}
          virtualize={true}
          virtualizerOptions={{
            getScrollElement: () => scrollRef?.current ?? null,
            measureElement: (element) => element.getBoundingClientRect().height,
            estimateSize: (row) => estimatedRowHeightByCards(row.original.cardsInfos),
          }}
          showBorderRadius
          showShadow
          showVisibility
          isOnWhite={false}
          showActionBar
          rowSelection={rowSelection}
          rowMarker={rowMarker}
          onRowSelectionChange={onRowSelectionChange}
          showSelect
          showSort
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={saveColumnVisibility}
          sorting={sorting}
          onSortingChange={saveSorting}
          onAddClick={handleAddCurriculum}
          bulkEditDropdownContent={bulkEdit}
          printerSettings={{
            headline: pimAuthClaims.getProfile()?.organization.name,
            subline: `${t('curriculum.title', { count: 2 })} - ${t('common.schoolYear')}`,
            filename: `${t('curriculum.title', { count: 2 })}_${schoolYear?.shortName}`,
          }}
          actionBarSettings={{
            showSelectedRowsCount: true,
            showExpertFilter: true,
            showPrintButton: true,
            showBulkEdit: true,
            disableBulkEdit: readonly,
            showAddButton: false,
            extendedActionsRight: (
              <Dropdown
                noPadding
                disabled={readonly}
                trigger={
                  <Button hierarchy='tertiary' icon={<AddIcon />} disabled={readonly}>
                    {t('common.add')}
                  </Button>
                }
              >
                <DropdownMenu data={addDropdownItems} />
              </Dropdown>
            ),
          }}
          columns={columns}
          lastColWidth='70px'
          lastCol={(row) => {
            return (
              <ButtonGroup disabled={readonly}>
                <Button
                  disabled={readonly}
                  isLoading={loadingState.some((ls) => ls.uuid === row.original.uuid && ls.loading)}
                  hierarchy='secondary'
                  onClick={() => {
                    setCurrentCurriculumUuid(row.original.uuid);
                    handleEditCurriculum(row.original.uuid);
                  }}
                  icon={<EditIcon className='small' />}
                />
                <Dropdown
                  disabled={readonly || loadingState.some((ls) => ls.uuid === row.original.uuid && ls.loading)}
                  trigger={
                    <Button disabled={readonly} hierarchy='secondary' icon={<DotsHorizontalIcon className='small' />} />
                  }
                >
                  <DropdownMenu data={dropdownItems(row)} />
                </Dropdown>
              </ButtonGroup>
            );
          }}
        />

        <CurriculumModal
          versionUuid={curriculumUuid}
          curriculumUuid={currentCurriculumUuid}
          isOpen={editModalOpen}
          onClose={handleClose}
        />

        <CurriculumBlockContainerModal
          versionUuid={curriculumUuid}
          curriculumUuid={currentCurriculumUuid}
          isOpen={epochModalOpen}
          onClose={handleClose}
        />

        <ConfirmationDialog />
      </>
    );
  },
);
